import { message } from 'antd'
import { useTranslation } from 'react-i18next'

import dy_logo from '@/assets/home/DYlogo.jpg'
import pointsNavIcon from '@/assets/points/points-nav.png'
import { NavMenuItem } from '@/network/api/global'
import { OfficialInfoData } from '@/network/api/global/type'
import { useAiMembershipStore } from '@/store/aiMembership'
import { useNavigationMenusStore } from '@/store/navigation-menus'
import { useUserStore } from '@/store/user'

import Account from './Account'
import Cart from './Cart'
import HeaderStyle from './index.module.scss'
import LanguageSwitcher from './LanguageSwitcher'

interface HeaderProps {
  officialData?: OfficialInfoData
}

function HeaderXzz({ officialData }: HeaderProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { currentNavigation, changeNavigation } = useNavigationStore((state) => state)
  const { i18n } = useTranslation()
  const token = useUserStore((state) => state.token)
  const availablePoints = useAiMembershipStore((state) => state.availablePoints)
  const { menus, fetchMenus } = useNavigationMenusStore((state) => state)

  const lang = i18n.language === 'zh-CN' ? 'zh' : i18n.language

  useEffect(() => {
    fetchMenus()
  }, [fetchMenus])

  const goPricing = () => navigate('/ai/pricing')
  const goMemberCenter = () => navigate('/ai/member')

  const getLabel = (item: NavMenuItem): string => {
    if (lang === 'zh') return item.title
    return item.title_i18n?.[lang] || item.title
  }

  const isMatch = (item: NavMenuItem, pathname: string): boolean => {
    const pattern = item.config?.matchPattern
    if (!pattern) return currentNavigation === item.path
    if (pattern.startsWith('prefix:')) {
      const prefix = pattern.slice(7)
      return pathname === prefix || pathname.startsWith(prefix + '/')
    }
    if (pattern.startsWith('exact:')) {
      return pathname === pattern.slice(6)
    }
    return pathname === item.path
  }

  useEffect(() => {
    const matched = menus.find((item) => isMatch(item, location.pathname))
    changeNavigation(matched?.path ?? location.pathname)
  }, [changeNavigation, location.pathname, menus])

  const changeItem = (item: NavMenuItem) => {
    if (item.config?.comingSoon) {
      message.info('功能开发中，敬请期待')
      return
    }
    if (item.config?.openInNewTab) {
      window.open(item.path, '_blank')
      return
    }
    if (location.pathname === item.path) return
    changeNavigation(item.path)
    navigate(item.path)
  }

  const renderNavItem = (item: NavMenuItem) => {
    const active = isMatch(item, location.pathname)
    const hasChildren = item.children && item.children.length > 0

    return (
      <div
        className={`${HeaderStyle.navItemWrap} ${HeaderStyle.eachitem} ${active ? HeaderStyle.active : ''} ${item.config?.badge ? HeaderStyle.aiItem : ''}`}
        style={{ fontSize: lang === 'en' ? '13px' : '15px' }}
        key={item.id}
        onClick={() => changeItem(item)}
      >
        {getLabel(item)}
        {item.config?.badge && <span className={HeaderStyle.aiBadge}>{item.config.badge}</span>}
        {hasChildren && <span className={HeaderStyle.arrow}>▾</span>}
        {hasChildren && (
          <div className={HeaderStyle.dropdown}>
            {item.children!.map((child) => (
              <div
                key={child.id}
                className={HeaderStyle.dropdownItem}
                onClick={(e) => {
                  e.stopPropagation()
                  changeItem(child)
                }}
              >
                {getLabel(child)}
                {child.config?.badge && <span className={HeaderStyle.aiBadge}>{child.config.badge}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={HeaderStyle.header}>
      <div className={HeaderStyle.leftbox}>
        <div className={HeaderStyle.logo} onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img
            src={officialData?.logoUrl || dy_logo}
            alt="logo"
            onError={(e) => {
              e.currentTarget.onerror = null
              e.currentTarget.src = dy_logo
            }}
          />
        </div>
        <div className={HeaderStyle.allitem}>{menus.map((item) => renderNavItem(item))}</div>
      </div>
      <div className={HeaderStyle.rightbox}>
        <LanguageSwitcher />
        {token ? (
          <div className={HeaderStyle.pointsBox}>
            <div
              className={HeaderStyle.pointsGroup}
              onClick={goMemberCenter}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') goMemberCenter()
              }}
              style={{ cursor: 'pointer' }}
            >
              <img className={HeaderStyle.pointsIcon} src={pointsNavIcon} alt="" />
              <span className={HeaderStyle.pointsValue}>{availablePoints}</span>
            </div>
            <button type="button" className={HeaderStyle.buyBtn} onClick={goPricing}>
              购买
            </button>
          </div>
        ) : null}
        <Cart />
        <Account />
      </div>
    </div>
  )
}

export default HeaderXzz
